import { useEffect, useRef, useState, type CSSProperties } from 'react';
import '../../styles/hero/flowBackground.css';
import type { Look } from '../../data/stageLooks';

/*
 * A flowing, silk-like background drawn by one small fragment shader (no 3D library): a field warped
 * by layered sine waves, lit like fabric with a key light and two accents, so the ridges catch light
 * and the surface reads as three-dimensional. Its four colours and its light or dark shading follow
 * the project on the device stage and cross-fade when it changes.
 *
 * Desktop only (a phone gets the CSS glow below it, which follows the same palette). Rendered at a
 * fraction of the screen resolution and scaled up; it pauses off screen and in a background tab, and
 * is drawn once, still, for reduced motion. If WebGL is unavailable the CSS glow stays.
 */
const VERTEX = `
attribute vec2 a;
void main() { gl_Position = vec4(a, 0.0, 1.0); }
`;

const FRAGMENT = `
precision highp float;
uniform vec2 u_res;
uniform float u_time;
uniform float u_light;
uniform vec2 u_mouse;
uniform vec3 u_c0;
uniform vec3 u_c1;
uniform vec3 u_c2;
uniform vec3 u_c3;

float field(vec2 p, float t) {
    vec2 q = p;
    for (int i = 0; i < 5; i++) {
        float fi = float(i);
        q += vec2(
            sin(q.y * (1.15 + fi * 0.32) + t * (0.34 + fi * 0.05) + fi * 1.7),
            cos(q.x * (1.05 + fi * 0.28) - t * (0.29 + fi * 0.04) + fi * 2.3)
        ) * (0.66 / (1.0 + fi * 0.55));
    }
    return 0.5 + 0.5 * sin(q.x * 1.25 + q.y * 1.05 + t * 0.16);
}

vec3 ramp(float h) {
    float t = smoothstep(0.2, 0.8, h) * 3.0;
    vec3 c = mix(u_c0, u_c1, smoothstep(0.0, 1.0, t));
    c = mix(c, u_c2, smoothstep(1.0, 2.0, t));
    return mix(c, u_c3, smoothstep(2.0, 3.0, t));
}

float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }

void main() {
    vec2 uv = gl_FragCoord.xy / u_res;
    vec2 p = (gl_FragCoord.xy - 0.5 * u_res) / min(u_res.x, u_res.y) * 2.1;
    p += u_mouse * 0.35;
    float t = u_time * 0.6;

    float e = 0.012;
    float h = field(p, t);
    float hx = field(p + vec2(e, 0.0), t);
    float hy = field(p + vec2(0.0, e), t);
    vec3 n = normalize(vec3(h - hx, h - hy, e * 1.2));

    vec3 L = normalize(vec3(-0.45, 0.55, 0.7));
    float diff = clamp(dot(n, L), 0.0, 1.0);
    float spec = pow(clamp(dot(reflect(-L, n), vec3(0.0, 0.0, 1.0)), 0.0, 1.0), 30.0);
    float rim = pow(1.0 - clamp(n.z, 0.0, 1.0), 2.2);

    // Light grounds are already bright: a lower curve keeps them from clipping to white.
    vec3 col = ramp(h) * mix(0.74 + 0.5 * diff, 0.6 + 0.36 * diff, u_light);
    col += u_c3 * rim * (0.35 - 0.12 * u_light);
    col += vec3(1.0) * spec * mix(0.62, 0.34, u_light);

    // Dark: deepened toward the edges.
    float vig = smoothstep(1.35, 0.25, length(uv - 0.5) * 1.4);
    col *= mix(0.55 + 0.45 * vig, 1.0, u_light);

    col += (hash(gl_FragCoord.xy + u_time) - 0.5) / 160.0;
    gl_FragColor = vec4(col, 1.0);
}
`;

type Rgb = [number, number, number];

function hexToRgb(hex: string): Rgb {
    const n = parseInt(hex.slice(1), 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

function compile(gl: WebGLRenderingContext, type: number, source: string) {
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        gl.deleteShader(shader);
        return null;
    }
    return shader;
}

interface FlowBackgroundProps {
    look: Look;
}

const FlowBackground = ({ look }: FlowBackgroundProps) => {
    const canvas = useRef<HTMLCanvasElement>(null);
    // Where the palette is heading; the draw loop eases toward it.
    const target = useRef({ colors: look.colors.map(hexToRgb) as Rgb[], light: look.mode === 'light' ? 1 : 0 });
    const redraw = useRef<(() => void) | null>(null);
    const [wide, setWide] = useState(false);
    const [live, setLive] = useState(false);

    useEffect(() => {
        const query = window.matchMedia('(min-width: 768px)');
        const update = () => setWide(query.matches);
        update();
        query.addEventListener('change', update);
        return () => query.removeEventListener('change', update);
    }, []);

    // A new project: steer the palette, and repaint at once if nothing is animating.
    useEffect(() => {
        target.current = { colors: look.colors.map(hexToRgb) as Rgb[], light: look.mode === 'light' ? 1 : 0 };
        redraw.current?.();
    }, [look]);

    useEffect(() => {
        const el = canvas.current;
        if (!el || !wide) return;

        const gl = el.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'low-power', failIfMajorPerformanceCaveat: true });
        if (!gl) return;
        const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX);
        const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
        const program = gl.createProgram();
        if (!vertex || !fragment || !program) return;
        gl.attachShader(program, vertex);
        gl.attachShader(program, fragment);
        gl.linkProgram(program);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;
        gl.useProgram(program);

        // One oversized triangle covers the screen.
        const buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
        const position = gl.getAttribLocation(program, 'a');
        gl.enableVertexAttribArray(position);
        gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

        const u = (name: string) => gl.getUniformLocation(program, name);
        const uRes = u('u_res');
        const uTime = u('u_time');
        const uLight = u('u_light');
        const uMouse = u('u_mouse');
        const uColors = [u('u_c0'), u('u_c1'), u('u_c2'), u('u_c3')];

        const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        // Enough for a smooth silk at a fraction of the cost; lowered further if the machine struggles.
        let scale = Math.min(window.devicePixelRatio || 1, 1.5) * 0.6;
        const resize = () => {
            const w = Math.max(2, Math.round(el.clientWidth * scale));
            const h = Math.max(2, Math.round(el.clientHeight * scale));
            if (el.width !== w || el.height !== h) {
                el.width = w;
                el.height = h;
                gl.viewport(0, 0, w, h);
            }
        };
        const observer = new ResizeObserver(() => {
            resize();
            draw();
        });

        const colors = target.current.colors.map((c) => [...c]) as Rgb[];
        let light = target.current.light;
        let time = 0;
        let mouseX = 0;
        let mouseY = 0;
        let pointerX = 0;
        let pointerY = 0;

        function draw() {
            gl!.uniform2f(uRes, el!.width, el!.height);
            gl!.uniform1f(uTime, time);
            gl!.uniform1f(uLight, light);
            gl!.uniform2f(uMouse, mouseX, mouseY);
            colors.forEach((c, i) => gl!.uniform3f(uColors[i], c[0], c[1], c[2]));
            gl!.drawArrays(gl!.TRIANGLES, 0, 3);
        }

        if (still) {
            // Reduced motion: one still frame, repainted when the palette changes.
            redraw.current = () => {
                target.current.colors.forEach((c, i) => (colors[i] = [...c] as Rgb));
                light = target.current.light;
                draw();
            };
        }

        resize();
        draw();
        setLive(true);
        observer.observe(el);

        let frame = 0;
        let last = 0;
        let visible = true;
        let sampled = 0;
        let slow = 0;
        const intersection = new IntersectionObserver(([entry]) => {
            visible = entry.isIntersecting;
        });
        intersection.observe(el);

        const onMove = (event: PointerEvent) => {
            pointerX = (event.clientX / window.innerWidth) * 2 - 1;
            pointerY = -((event.clientY / window.innerHeight) * 2 - 1);
        };

        const tick = (now: number) => {
            frame = window.requestAnimationFrame(tick);
            const dt = Math.min((now - last) / 1000, 0.1);
            last = now;
            if (!visible || document.hidden || dt === 0) return;

            // Ease the palette toward the project on show (about 0.8s to settle).
            const k = 1 - Math.exp(-dt * 4);
            target.current.colors.forEach((c, i) => {
                colors[i][0] += (c[0] - colors[i][0]) * k;
                colors[i][1] += (c[1] - colors[i][1]) * k;
                colors[i][2] += (c[2] - colors[i][2]) * k;
            });
            light += (target.current.light - light) * k;

            time += dt;
            mouseX += (pointerX - mouseX) * 0.04;
            mouseY += (pointerY - mouseY) * 0.04;
            draw();

            // If the first second of frames is slow, draw smaller instead of stuttering.
            if (sampled < 60) {
                sampled += 1;
                if (dt > 0.034) slow += 1;
                if (sampled === 60 && slow > 30 && scale > 0.3) {
                    scale *= 0.62;
                    resize();
                }
            }
        };

        if (!still) {
            window.addEventListener('pointermove', onMove, { passive: true });
            frame = window.requestAnimationFrame(tick);
        }

        // The context can be taken away (tab discarded, GPU reset): fall back to the CSS glow.
        const onLost = (event: Event) => {
            event.preventDefault();
            setLive(false);
        };
        el.addEventListener('webglcontextlost', onLost);

        return () => {
            redraw.current = null;
            window.cancelAnimationFrame(frame);
            window.removeEventListener('pointermove', onMove);
            el.removeEventListener('webglcontextlost', onLost);
            observer.disconnect();
            intersection.disconnect();
            gl.deleteBuffer(buffer);
            gl.deleteProgram(program);
            gl.deleteShader(vertex);
            gl.deleteShader(fragment);
            gl.getExtension('WEBGL_lose_context')?.loseContext();
            setLive(false);
        };
    }, [wide]);

    const style = {
        '--flow-c0': look.colors[0],
        '--flow-c1': look.colors[1],
        '--flow-c2': look.colors[2],
        '--flow-c3': look.colors[3],
    } as CSSProperties;

    return (
        <div className="flow" style={style}>
            <div className="flow__glow" />
            {wide && <canvas ref={canvas} className={`flow__canvas${live ? ' is-live' : ''}`} />}
        </div>
    );
};

export default FlowBackground;
